"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Banner } from "@/components/sections/Banner";
import { getCloudinaryUrl } from "@/lib/cloudinary";
import { BreadcrumbHomepage } from "@/components/sections/breadcrumb-homepage/BreadcrumbHomepage";
import { apiClient } from "@/utils/ky";
import Image from "next/image";
type GiftCardData = {
  id: number;
  cardCode: string;
  senderName: string;
  recipientName: string;
  senderEmail: string;
  recipientEmail: string;
  cardValue: number;
  remainingValue: number;
  expirationDate: string;
  purchaseDate: string;
  available: boolean;
  design_image: string;
};

// Fonction pour sauvegarder avec expiration (24h par exemple)
const saveToLocalStorage = (data: GiftCardData) => {
  const item = {
    value: data,
    expiry: new Date().getTime() + 24 * 60 * 60 * 1000, // 24 heures
  };
  localStorage.setItem("giftCardSuccess", JSON.stringify(item));
};

// Fonction pour récupérer en vérifiant l'expiration
const getFromLocalStorage = () => {
  const saved = localStorage.getItem("giftCardSuccess");
  if (!saved) return null;

  const item = JSON.parse(saved);
  if (new Date().getTime() > item.expiry) {
    localStorage.removeItem("giftCardSuccess");
    return null;
  }
  return item.value;
};

function SuccessPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [giftCardData, setGiftCardData] = useState<GiftCardData | null>(() => {
    if (typeof window !== "undefined") {
      return getFromLocalStorage();
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);

  // Effet pour gérer la navigation
  useEffect(() => {
    return () => {
      if (pathname !== "/carte-cadeau/success") {
        localStorage.removeItem("giftCardSuccess");
      }
    };
  }, [pathname]);

  // Effet pour la validation de session
  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    const validateSession = async () => {
      if (!sessionId) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await apiClient
          .get(`gift-cards/success?session_id=${sessionId}`)
          .json<GiftCardData>();

        setGiftCardData(data);
        saveToLocalStorage(data);
        window.history.replaceState({}, "", "/carte-cadeau/success");
      } catch (error) {
        console.error("Erreur lors de la validation:", error);
      }
      setIsLoading(false);
    };

    validateSession();
  }, [searchParams]);

  // Afficher une page blanche pendant le chargement initial
  if (isLoading) {
    return <div className="min-h-screen w-full bg-white" />;
  }

  // Si pas de données, afficher un message UX
  if (!giftCardData) {
    return (
      <main className="flex min-h-screen w-full flex-col items-center">
        <Banner
          title="Page non disponible"
          backgroundImage={getCloudinaryUrl("carte-cadeau-banner")}
        />
        <BreadcrumbHomepage />

        <div className="flex w-full flex-col items-center justify-center gap-8 px-4 py-20 text-center">
          <div className="max-w-2xl space-y-6">
            <h2 className="text-3xl font-semibold text-gray-900">
              Cette page n&apos;est plus accessible
            </h2>

            <p className="text-lg text-gray-600">
              Pour acheter une nouvelle carte cadeau, veuillez retourner à la
              page des cartes cadeaux.
            </p>

            <button
              onClick={() => router.push("/carte-cadeau")}
              className="mt-8 rounded-md border border-gray-900 bg-gray-900 px-8 py-3 text-lg font-medium text-white transition-colors hover:bg-gray-800"
            >
              Voir les cartes cadeaux
            </button>
          </div>
        </div>
      </main>
    );
  }

  // Afficher la page de succès avec les données
  return (
    <main className="flex min-h-screen w-full flex-col items-center">
      <Banner
        title="Merci pour votre achat !"
        backgroundImage={getCloudinaryUrl("carte-cadeau-banner")}
      />
      <BreadcrumbHomepage />

      <div className="flex w-full flex-col items-center justify-center gap-8 px-4 py-20 text-center">
        <div className="max-w-2xl space-y-6">
          <h2 className="text-3xl font-semibold text-gray-900">
            Votre carte cadeau a été envoyée avec succès !
          </h2>

          <div className="space-y-4 rounded-lg bg-gray-50 p-6 text-left">
            <p className="text-lg text-gray-700">
              Cher(e){" "}
              <span className="font-semibold">{giftCardData?.senderName}</span>,
            </p>
            <p className="text-gray-700">
              Votre carte cadeau d&apos;une valeur de{" "}
              <span className="font-semibold">{giftCardData?.cardValue}€</span>{" "}
              a été envoyée à{" "}
              <span className="font-semibold">
                {giftCardData?.recipientName}
              </span>
              .
            </p>
            <p className="text-gray-700">
              Un email de confirmation vous a été envoyé à l&apos;adresse :{" "}
              <span className="font-semibold">{giftCardData?.senderEmail}</span>
            </p>
            <p className="text-gray-700">
              Le destinataire recevra la carte cadeau à l&apos;adresse :{" "}
              <span className="font-semibold">
                {giftCardData?.recipientEmail}
              </span>
            </p>
            <div className="mt-4 rounded-md bg-amber-50 p-4">
              <p className="text-amber-800">
                ⚠️ Cette carte cadeau sera valable jusqu&apos;au{" "}
                <span className="font-semibold">
                  {new Date(giftCardData?.expirationDate).toLocaleDateString(
                    "fr-FR",
                    {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    },
                  )}
                </span>
              </p>
            </div>
            <div className="mt-4">
              <Image
                src={giftCardData?.design_image}
                alt="Design de la carte cadeau"
                className="w-full rounded-lg"
              />
            </div>
          </div>

          <div className="space-y-4 rounded-lg bg-gray-100 p-6">
            <p className="text-gray-700">
              Le destinataire recevra la carte cadeau dans les prochaines
              minutes. Si vous ne voyez pas l&apos;email, pensez à vérifier vos
              spams.
            </p>
          </div>

          <button
            onClick={() => router.push("/carte-cadeau")}
            className="mt-8 rounded-md border border-gray-900 bg-gray-900 px-8 py-3 text-lg font-medium text-white transition-colors hover:bg-gray-800"
          >
            Retour aux cartes cadeaux
          </button>
        </div>
      </div>
    </main>
  );
}

export default function SuccessPage() {
  return (
    <Suspense fallback={<div className="min-h-screen w-full bg-white" />}>
      <SuccessPageContent />
    </Suspense>
  );
}
